import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as dayjs from 'dayjs';
import * as timezone from 'dayjs/plugin/timezone';
import * as utc from 'dayjs/plugin/utc';
import { RouteReplayService } from '../../services/route-replay.service';
import { RouteResponse, DatePickerConfig } from '../../models/route-selection.model';
import { DropdownItem } from 'mis-crystal-design-system/dropdown';
import { RouteReplayDataService } from '../../services/route-replay-data.service';
import { ToastService } from 'mis-crystal-design-system/toast';
import { ColorService } from '../../services/color.service';
import { RouteSelectionStateService } from '../../services/route-selection-state.service';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

@Component({
  selector: 'app-shift-route-selection',
  templateUrl: './shift-route-selection.component.html',
  styleUrl: './shift-route-selection.component.scss'
})
export class ShiftRouteSelectionComponent implements OnInit {
  private routeReplayService=inject(RouteReplayService);
  private dataService=inject(RouteReplayDataService);
  private toast=inject(ToastService);
  private activatedRoute=inject(ActivatedRoute);
  private colorService=inject(ColorService);
  private stateService=inject(RouteSelectionStateService);
  
  selectedDate: string = '';
  selectedShift: DropdownItem={label:'',value:''};
  selectedRoutes: DropdownItem[] = [];
  selectedDirections: DropdownItem[] = [];
  
  buid: string = '';
  source: string = '';
  
  shiftOptions: DropdownItem[] = [
    { label: 'Shift A', value: 'A' },
    { label: 'Shift B', value: 'B' },
    { label: 'Shift C', value: 'C' },
    { label: 'Shift G', value: 'G' }
  ];
  
  directionOptions: DropdownItem[] = [
    { label: 'Login', value: 'Login' },
    { label: 'Logout', value: 'Logout' }
  ];
  
  allRoutes: RouteResponse[] = [];
  routeOptions: DropdownItem[] = [];
  isLoading: boolean = false;
  
  datepickerConfig: DatePickerConfig;

  constructor(
    private router: Router,
  ) {
    // Initialize datepicker config in constructor
    this.datepickerConfig = this.getDatepickerConfig();
  }

  ngOnInit(): void {
    this.colorService.reset();
    
    // Get query parameters
    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.buid = queryParams['buid'] || '';
    this.source = queryParams['source'] || '';
    
    // Update data service with buid
    if (this.buid) {
      this.dataService.updateBuid(this.buid, this.source);
    }
    
    if (this.stateService.hasState()) {
      const savedState = this.stateService.getState();
      this.selectedDate = savedState.selectedDate;
      this.selectedShift = savedState.selectedShift;
      this.selectedDirections = savedState.selectedDirections.length > 0 
        ? savedState.selectedDirections 
        : [...this.directionOptions];
      
      if (this.selectedDate && this.selectedShift && this.selectedShift.value) {
        this.fetchRoutes(savedState.selectedRoutes || []);
      }
      
    } else {
      this.selectedDirections = [...this.directionOptions];
      
      if (this.selectedDate && this.selectedShift && this.selectedShift.value) {
        this.fetchRoutes();
      }
    }
    
    // Ensure datepicker config is set
    this.datepickerConfig = this.getDatepickerConfig();
  }

  getDatepickerConfig(): DatePickerConfig {
    return {
      format: 'DD/MM/YYYY',
      timezone: 'Asia/Kolkata',
      minDate: null,
      maxDate: dayjs().format('DD/MM/YYYY')
    };
  }

  displayFormat(date: string): string {
    if (!date) return '';
    return date;
  }

  getCDSSelectedDate(): string {
    return this.selectedDate;
  }

  onDateSelected(event: any): void {
    if (!event) return;
    
    let dateValue: dayjs.Dayjs;
    
    if (typeof event === 'string') {
      dateValue = dayjs(event, 'DD/MM/YYYY');
    } else if (dayjs.isDayjs(event)) {
      dateValue = event;
    } else {
      dateValue = dayjs(event);
    }
    
    if (!dateValue.isValid()) {
      console.error('Invalid date selected:', event);
      return;
    }
    
    this.selectedDate = dateValue.format('DD/MM/YYYY');
    this.fetchRoutesIfReady();
  }

  onShiftChange(event: DropdownItem): void {
    this.selectedShift = event;
    this.fetchRoutesIfReady();
  }

  onRouteSelectionChange(event: DropdownItem[]): void {
    this.selectedRoutes = event;
  }

  onDirectionChange(event: DropdownItem[]): void {
    this.selectedDirections = event;
    this.updateRouteOptions();
    
    this.cleanupSelectedRoutes();
  }

  updateRouteOptions(): void {
    if (!this.allRoutes || this.allRoutes.length === 0) {
      this.routeOptions = [];
      return;
    }

    let filteredRoutes = [...this.allRoutes];
    
    if (this.selectedDirections.length === 0 || this.selectedDirections.length === 2) {
      filteredRoutes = [...this.allRoutes];
    } else if (this.selectedDirections.length === 1) {
      const selectedValue = this.selectedDirections[0].value;
      filteredRoutes = this.allRoutes.filter(route => {
        if (!route.direction || route.direction.trim() === '') {
          return true;
        }
        return route.direction === selectedValue;
      });
    }

    this.routeOptions = filteredRoutes.map(route => ({
      label: `${route.routeName} (${route.vehicles} vehicles)`,
      value: route.routeId
    }));
  }

  cleanupSelectedRoutes(): void {
    const validRouteIds = this.routeOptions.map(option => option.value);
    
    this.selectedRoutes = this.selectedRoutes.filter(selectedRoute => 
      validRouteIds.includes(selectedRoute.value)
    );
  }

  fetchRoutesIfReady(): void {
    if (this.selectedDate && this.selectedShift && this.selectedShift.value) {
      this.fetchRoutes();
    }
  }

  fetchRoutes(savedSelectedRoutes?: DropdownItem[]): void {
    if (!this.selectedDate || !this.selectedShift || !this.selectedShift.value) return;
    
    this.isLoading = true;
    this.allRoutes = [];
    this.routeOptions = [];
    this.selectedRoutes = [];

    const apiDate = dayjs(this.selectedDate, 'DD/MM/YYYY').format('DD-MM-YYYY');

    const payload = {
      date: apiDate,
      shift: this.selectedShift.value
    };

    this.routeReplayService.getRouteDetails(payload).subscribe({
      next: (routes: RouteResponse[]) => {
        this.allRoutes = routes;
        this.updateRouteOptions();
        if (savedSelectedRoutes && savedSelectedRoutes.length > 0) {
            const validRouteIds = this.routeOptions.map(option => option.value);
            this.selectedRoutes = savedSelectedRoutes.filter(route => 
              validRouteIds.includes(route.value)
            );
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toast.displayMsg("Error Fetching Routes !")
      }
    });
  }

  isFormValid(): boolean {
    return !!(this.selectedDate && this.selectedShift && this.selectedRoutes && this.selectedRoutes.length > 0);
  }

  isRouteDropdownDisabled(): boolean {
    return this.isLoading || this.routeOptions.length === 0 || !this.selectedDate || !this.selectedShift || !this.selectedShift.value;
  }

  onShow(): void {
    if (!this.isFormValid()) return;
    
    this.stateService.saveState({
      selectedDate: this.selectedDate,
      selectedShift: this.selectedShift,
      selectedDirections: this.selectedDirections,
      selectedRoutes: this.selectedRoutes,
      allRoutes: this.allRoutes
    });
    
    // Join route IDs with commas
    const routeIds = this.selectedRoutes.map(r => r.value).join(',');
    
    this.router.navigate(['/route-replay/shuttle/routes'], {
      queryParams: {
        routeIds: routeIds,
        date: this.selectedDate,
        shift: this.selectedShift!.value,
        buid: this.buid,
        source: this.source
      }
    });
  }

  onCancel(): void {
    this.closeIframe('Route replay cancelled');
  }

  private closeIframe(message: string): void {
    const parentWindow = window.parent;
    const data = {
      action: 'closeIframe',
      message: message
    };
    console.log('Closing iframe with data', data);
    parentWindow.postMessage(data, '*');
  }
}
